import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';

interface AuthenticatedUser {
  email: string;
  name: string;
  picture: string | null;
  sub?: string | null;
  [key: string]: any;
}

interface AuthContextType {
  token: string | null;
  user: AuthenticatedUser | null;
  login: (accessProfile?: { user?: AuthenticatedUser | null } | null) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const isDevelopment = process.env.NODE_ENV === 'development';
const API_BASE_URL = process.env.REACT_APP_API_URL || (isDevelopment ? 'http://localhost:7777' : '');
const COOKIE_SESSION_SENTINEL = 'cookie-session';

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    localStorage.removeItem('authToken');

    const originalFetch = window.fetch.bind(window);
    window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
      const url =
        typeof input === 'string'
          ? input
          : input instanceof URL
            ? input.toString()
            : input.url;
      const isApiRequest = url.startsWith('/api/') || (API_BASE_URL && url.startsWith(API_BASE_URL));
      if (!isApiRequest) {
        return originalFetch(input, init);
      }

      const headers = new Headers(
        init?.headers || (input instanceof Request ? input.headers : undefined)
      );
      if (headers.get('Authorization') === `Bearer ${COOKIE_SESSION_SENTINEL}`) {
        headers.delete('Authorization');
      }

      return originalFetch(input, {
        ...init,
        headers,
        credentials: init?.credentials || 'include',
      });
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, []);

  const refreshSession = async () => {
    try {
      setIsLoading(true);
      const response = await fetchWithTimeout(
        `${API_BASE_URL}/api/auth/me`,
        {
          cache: 'no-store',
          credentials: 'include',
        },
        1500,
        'Timed out while refreshing the signed-in session'
      );

      if (!response.ok) {
        setToken(null);
        setUser(null);
        return;
      }

      const accessProfile = await response.json();
      if (accessProfile?.user) {
        setToken(COOKIE_SESSION_SENTINEL);
        setUser(accessProfile.user);
      } else {
        setToken(null);
        setUser(null);
      }
    } catch (error) {
      console.error('[AUTH] Failed to refresh session:', error);
      setToken(null);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void refreshSession();
  }, []);

  const login = async (accessProfile?: { user?: AuthenticatedUser | null } | null) => {
    if (accessProfile?.user) {
      setToken(COOKIE_SESSION_SENTINEL);
      setUser(accessProfile.user);
      setIsLoading(false);
      return;
    }

    await refreshSession();
  };

  const logout = async () => {
    try {
      await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('[AUTH] Logout request failed:', error);
    }

    setToken(null);
    setUser(null);
    setIsLoading(false);
  };

  const isAuthenticated = !!token;

  return (
    <AuthContext.Provider value={{ token, user, login, logout, isAuthenticated, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
